/* eslint-disable jira/import-whitelist */

import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { getTraceIdJq } from '@atlassian/jira-fetch/src/utils/trace-id.tsx';
import { getPrefixedMarkName } from '@atlassian/jira-providers-spa-apdex-analytics/src/transform.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import type {
	RouterDataContext,
	Query,
	Match,
	ResourceStoreContext,
} from '@atlassian/react-resource-router';
import { getActiveQuickFilters } from '../../../utils/sticky-quickfilters/index.tsx';
import { buildAllDataQueryParams, getInlineScriptData } from '../../utils/index.tsx';
import BoardCache from '../utils/board-cache.tsx';
import { handleAbort } from '../utils/handle-abort.tsx';
import { isBoardRoute, isBacklogRoute } from '../utils/is-route.tsx';

type EndPoint = string | null;

const getEndpoint = (baseUrl: string, query: Query, match: Match, type: string): EndPoint => {
	if (isBacklogRoute(type)) {
		return `/rest/greenhopper/1.0/xboard/plan/v2/backlog/data.json?${buildAllDataQueryParams(query, match)}`;
	}
	if (isBoardRoute(type)) {
		return `/rest/greenhopper/1.0/xboard/work/allData.json?${buildAllDataQueryParams(
			query,
			match,
		)}`;
	}
	return null;
};

const getStartMarkName = (type: string): EndPoint => {
	if (isBacklogRoute(type)) {
		return getPrefixedMarkName('backlog_data_initiated');
	}
	if (isBoardRoute(type)) {
		return getPrefixedMarkName('board_data_initiated');
	}
	return null;
};

const getMarkName = (type: string): EndPoint => {
	if (isBacklogRoute(type)) {
		return getPrefixedMarkName('backlog_data_done');
	}
	if (isBoardRoute(type)) {
		return getPrefixedMarkName('board_data_done');
	}
	return null;
};

const getData = ({
	query,
	match,
	tenantContext,
	signal,
	name,
}: {
	query: Query;
	match: Match;
	tenantContext: ResourceStoreContext['tenantContext'];
	signal: AbortSignal;
	name: string;
}) => {
	const { baseUrl } = tenantContext;
	const endpoint = getEndpoint(baseUrl, query, match, name);
	const INITIAL_DATA_DONE = getMarkName(name);
	try {
		if (endpoint !== null && INITIAL_DATA_DONE !== null) {
			return (
				getInlineScriptData('SPA_RAPIDBOARD') ||
				fetchJson(endpoint, {
					signal,
				})
			);
		}
		throw new FetchError(
			400,
			`An error was thrown while prefetching initial all data due to ${
				!endpoint ? 'endpoint' : `${INITIAL_DATA_DONE} mark`
			} missing`,
		);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.classic-projects.initial-all-data.getData',
				message: 'An error was thrown while prefetching initial all data',
				name: 'ResourceError',
			},
			error,
		);
	}
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default async (
	{ query, route, match }: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
) => {
	if ((isBoardRoute(route?.name) || isBacklogRoute(route?.name)) && !query.config) {
		return null;
	}

	const boardId = match.params.boardId !== undefined ? match.params.boardId : query.rapidView;
	if (typeof boardId === 'undefined') {
		return null;
	}

	const { name = '' } = route || {};

	BoardCache.initBoard(boardId, isBoardRoute(name));
	const etag = await BoardCache.getETag();

	let { quickFilter } = query;
	const getDataQuery = {
		...query,
		etag,
	};
	quickFilter = quickFilter !== undefined ? quickFilter : getActiveQuickFilters(name);
	// @ts-expect-error - TS2339 - Property 'quickFilter' does not exist on type '{ etag: any; }'.
	if (quickFilter?.length) getDataQuery.quickFilter = quickFilter;

	const signal = handleAbort();

	try {
		const startMark = getStartMarkName(name);
		if (typeof startMark === 'string') {
			setMark(startMark);
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const allDataXHR = window.SPA_RAPIDBOARD;
		const promise = getData({
			query: getDataQuery,
			match,
			tenantContext,
			signal,
			name,
		});

		const response = await promise;
		const allDataTraceId = getTraceIdJq(allDataXHR);
		await BoardCache.setETag(response);

		const mark = getMarkName(name);
		if (typeof mark === 'string') {
			setMark(mark);
		}

		return {
			data: promise,
			route: name,
			allDataTraceId,
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.classic-projects.initial-all-data.getData',
				message: 'An error was thrown while prefetching initial all data',
				name: 'ResourceError',
			},
			error,
		);
	}
};
